// AuditDebugger - Handles debugging, error recovery, and data integrity
export class AuditDebugger {
  constructor(auditService) {
    this.auditService = auditService;
    this.debugLog = [];
    this.errorHistory = [];
  }

  // Analyze data structure for debugging
  analyzeDataStructure(data) {
    const analysis = {
      totalCategories: 0,
      totalControlItems: 0,
      totalChecks: 0,
      failedChecks: 0,
      passedChecks: 0,
      dataFormat: 'unknown',
      isIndependent: true,
      structureValidation: {
        hasCategories: false,
        hasControlItems: false,
        hasChecks: false,
        isWellFormed: true,
        errors: []
      }
    };

    if (!data || typeof data !== 'object') {
      analysis.structureValidation.errors.push('Data is not an object');
      analysis.structureValidation.isWellFormed = false;
      return analysis;
    }

    analysis.dataFormat = this.auditService.transformer.isCumulativeDataFormat(data) ? 'cumulative' : 'legacy';
    analysis.totalCategories = Object.keys(data).length;
    analysis.structureValidation.hasCategories = analysis.totalCategories > 0;

    Object.keys(data).forEach(categoryId => {
      const categoryData = data[categoryId];
      
      if (!categoryData || typeof categoryData !== 'object') {
        analysis.structureValidation.errors.push(`Category ${categoryId} is invalid`);
        analysis.structureValidation.isWellFormed = false;
        return;
      }
      
      if (categoryData.controlItems) {
        analysis.structureValidation.hasControlItems = true;
        const controlItemKeys = Object.keys(categoryData.controlItems);
        analysis.totalControlItems += controlItemKeys.length;
        
        controlItemKeys.forEach(controlItemId => {
          const controlItemData = categoryData.controlItems[controlItemId];
          
          if (!controlItemData || typeof controlItemData !== 'object') {
            analysis.structureValidation.errors.push(`Control item ${controlItemId} in category ${categoryId} is invalid`);
            analysis.structureValidation.isWellFormed = false;
            return;
          }
          
          if (controlItemData.checks) {
            analysis.structureValidation.hasChecks = true;
            const checkKeys = Object.keys(controlItemData.checks);
            analysis.totalChecks += checkKeys.length;
            
            checkKeys.forEach(checkId => {
              const checkData = controlItemData.checks[checkId];
              
              if (!checkData || typeof checkData !== 'object') {
                analysis.structureValidation.errors.push(`Check ${checkId} in control item ${controlItemId} is invalid`);
                analysis.structureValidation.isWellFormed = false;
                return;
              }
              
              if (analysis.dataFormat === 'cumulative') {
                if (checkData.hasOwnProperty('isChecked')) {
                  if (checkData.isChecked) {
                    analysis.failedChecks++;
                  } else {
                    analysis.passedChecks++;
                  }
                } else {
                  analysis.structureValidation.errors.push(`Check ${checkId} missing isChecked property`);
                }
              } else {
                if (checkData.isCompleted) {
                  if (checkData.isChecked) {
                    analysis.failedChecks++;
                  } else {
                    analysis.passedChecks++;
                  }
                }
              }
            });
          } else {
            analysis.structureValidation.errors.push(`Control item ${controlItemId} in category ${categoryId} has no checks`);
          }
        });
      } else {
        analysis.structureValidation.errors.push(`Category ${categoryId} has no control items`);
      }
    });

    // Log analysis for debugging
    this.debugLog.push({
      timestamp: new Date().toISOString(),
      type: 'data_analysis',
      analysis
    });

    console.log('📊 Independent Data Structure Analysis:', analysis);
    return analysis;
  }

  // Generate comprehensive debug report
  generateDebugReport(includeData = false) {
    const report = {
      timestamp: new Date().toISOString(),
      reportId: `debug_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      serviceInfo: {
        currentAuditResponse: this.auditService.currentAuditResponse?.audit_form_response_id || null,
        isDraftMode: this.auditService.isDraftMode,
        independentMode: this.auditService.independentMode
      },
      performance: this.auditService.performanceMonitor.getPerformanceMetrics(),
      sessions: {
        currentSessionId: this.auditService.sessionManager.getCurrentSessionId(),
        allSessions: this.auditService.sessionManager.getAllStoredSessions().map(s => ({
          sessionId: s.sessionId,
          status: s.auditResponse.status,
          formName: s.auditResponse.audit_form?.form_name,
          createdAt: s.auditResponse.created_at,
          hasData: !!s.auditResponse.raw_response_json?.cumulativeAuditData
        })),
        sessionStatistics: this.auditService.sessionManager.getSessionStatistics()
      },
      queues: {
        saveQueueSize: this.auditService.saveQueue.size,
        saveQueueEntries: Array.from(this.auditService.saveQueue.entries()).map(([key, data]) => ({
          key,
          responseId: data.responseId,
          timestamp: data.timestamp,
          age: Date.now() - data.timestamp
        })),
        timestampTracking: Array.from(this.auditService.lastSaveTimestamp.entries()).map(([id, timestamp]) => ({
          responseId: id,
          lastSave: timestamp,
          age: Date.now() - timestamp
        }))
      },
      systemHealth: this.auditService.performanceMonitor.metrics.lastHealthCheck || 'No health check performed',
      debugHistory: this.debugLog.slice(-20), // Last 20 debug entries
      errorHistory: this.errorHistory.slice(-10), // Last 10 errors
      browserInfo: this.getBrowserDebugInfo()
    };
    
    if (includeData && this.auditService.currentAuditResponse) {
      report.currentAuditData = {
        auditFormResponseId: this.auditService.currentAuditResponse.audit_form_response_id,
        status: this.auditService.currentAuditResponse.status,
        formInfo: this.auditService.currentAuditResponse.audit_form,
        rawResponseJson: this.auditService.currentAuditResponse.raw_response_json,
        dataAnalysis: this.auditService.currentAuditResponse.raw_response_json?.cumulativeAuditData ?
          this.analyzeDataStructure(this.auditService.currentAuditResponse.raw_response_json.cumulativeAuditData) : null
      };
    }
    
    // Log the report generation
    this.debugLog.push({
      timestamp: new Date().toISOString(),
      type: 'debug_report_generated',
      reportId: report.reportId,
      includeData
    });
    
    return report;
  }

  // Get browser-specific debug information
  getBrowserDebugInfo() {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      storage: {
        localStorageAvailable: typeof Storage !== 'undefined',
        localStorageSize: this.calculateLocalStorageSize(),
        sessionStorageAvailable: typeof sessionStorage !== 'undefined'
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      timestamp: new Date().toISOString()
    };
  }

  // Calculate localStorage usage
  calculateLocalStorageSize() {
    let totalSize = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        totalSize += localStorage[key].length + key.length;
      }
    }
    return {
      bytes: totalSize,
      kb: Math.round(totalSize / 1024),
      mb: Math.round(totalSize / (1024 * 1024))
    };
  }

  // Emergency recovery procedures
  async emergencyRecovery() {
    console.log('🚨 Starting emergency recovery...');
    
    const recovery = {
      timestamp: new Date().toISOString(),
      recoveryId: `recovery_${Date.now()}`,
      actions: [],
      recovered: [],
      errors: [],
      preRecoveryState: this.generateDebugReport(false)
    };
    
    try {
      // Step 1: Force save all pending data
      console.log('🚀 Step 1: Force saving all pending data...');
      await this.auditService.forceSaveAll();
      recovery.actions.push('Forced save of all pending data');
      
      // Step 2: Validate all stored sessions
      console.log('🔍 Step 2: Validating stored sessions...');
      const sessions = this.auditService.sessionManager.getAllStoredSessions();
      for (const session of sessions) {
        try {
          const summary = this.auditService.sessionManager.getSessionSummary(session.sessionId);
          if (summary) {
            // Test data integrity
            if (summary.hasData) {
              const analysis = this.analyzeDataStructure(
                session.auditResponse.raw_response_json?.cumulativeAuditData
              );
              if (analysis.structureValidation.isWellFormed) {
                recovery.recovered.push({
                  sessionId: session.sessionId,
                  status: 'valid',
                  summary
                });
              } else {
                recovery.errors.push(`Session ${session.sessionId}: Data structure issues - ${analysis.structureValidation.errors.join(', ')}`);
              }
            } else {
              recovery.recovered.push({
                sessionId: session.sessionId,
                status: 'empty_but_valid',
                summary
              });
            }
          }
        } catch (error) {
          recovery.errors.push(`Session ${session.sessionId}: Validation failed - ${error.message}`);
          
          // Try to recover by removing corrupted session
          try {
            const sessionStorageKey = `auditResponse_${session.sessionId}`;
            localStorage.removeItem(sessionStorageKey);
            recovery.actions.push(`Removed corrupted session: ${session.sessionId}`);
          } catch (cleanupError) {
            recovery.errors.push(`Failed to cleanup session ${session.sessionId}: ${cleanupError.message}`);
          }
        }
      }
      
      // Step 3: Clean up old sessions
      console.log('🧹 Step 3: Cleaning up old sessions...');
      const cleanedCount = this.auditService.sessionManager.cleanupOldSessions(24 * 60 * 60 * 1000); // 1 day
      recovery.actions.push(`Cleaned up ${cleanedCount} old sessions`);
      
      // Step 4: Reset service state
      console.log('🔄 Step 4: Resetting service state...');
      this.auditService.saveQueue.clear();
      this.auditService.lastSaveTimestamp.clear();
      recovery.actions.push('Cleared internal queues');
      
      // Step 5: Verify current audit response
      if (this.auditService.currentAuditResponse) {
        try {
          console.log('✅ Step 5: Verifying current audit response...');
          const verificationResult = await this.auditService.getAuditResponse(
            this.auditService.currentAuditResponse.audit_form_response_id
          );
          
          if (verificationResult.success) {
            this.auditService.currentAuditResponse = verificationResult.auditResponse;
            recovery.actions.push('Verified and refreshed current audit response');
          } else {
            recovery.errors.push(`Current audit verification failed: ${verificationResult.error}`);
            this.auditService.currentAuditResponse = null;
            this.auditService.isDraftMode = false;
            recovery.actions.push('Cleared invalid current audit response');
          }
        } catch (error) {
          recovery.errors.push(`Current audit verification error: ${error.message}`);
        }
      }
      
      // Step 6: Run health check
      console.log('🏥 Step 6: Running post-recovery health check...');
      const healthCheck = await this.auditService.performanceMonitor.healthCheck();
      recovery.postRecoveryHealth = healthCheck;
      recovery.actions.push('Performed post-recovery health check');
      
      recovery.success = recovery.errors.length < recovery.actions.length;
      recovery.summary = {
        totalActions: recovery.actions.length,
        totalErrors: recovery.errors.length,
        recoveredSessions: recovery.recovered.length,
        finalHealth: healthCheck.status
      };
      
      console.log('✅ Emergency recovery completed');
      console.log(`📊 Recovery summary: ${recovery.summary.totalActions} actions, ${recovery.summary.totalErrors} errors`);
      
    } catch (error) {
      recovery.errors.push(`Recovery failed: ${error.message}`);
      recovery.success = false;
      console.error('❌ Emergency recovery failed:', error);
    }
    
    // Log recovery attempt
    this.debugLog.push({
      timestamp: new Date().toISOString(),
      type: 'emergency_recovery',
      recovery
    });
    
    return recovery;
  }

  // Comprehensive data integrity verification
  async verifyDataIntegrity() {
    console.log('🔍 Verifying comprehensive data integrity...');
    
    const verification = {
      timestamp: new Date().toISOString(),
      verificationId: `integrity_${Date.now()}`,
      sessions: [],
      issues: [],
      summary: {
        totalSessions: 0,
        validSessions: 0,
        corruptedSessions: 0,
        missingData: 0,
        serverMismatches: 0
      },
      serverVerification: {
        attempted: 0,
        successful: 0,
        failed: 0
      }
    };
    
    const sessions = this.auditService.sessionManager.getAllStoredSessions();
    verification.summary.totalSessions = sessions.length;
    
    for (const session of sessions) {
      const sessionVerification = {
        sessionId: session.sessionId,
        isValid: true,
        issues: [],
        dataAnalysis: null,
        serverStatus: 'not_checked'
      };
      
      try {
        // Check basic structure
        if (!session.auditResponse.audit_form_response_id) {
          sessionVerification.issues.push('Missing audit_form_response_id');
          sessionVerification.isValid = false;
        }
        
        if (!session.auditResponse.status) {
          sessionVerification.issues.push('Missing status');
          sessionVerification.isValid = false;
        }
        
        // Check data consistency
        if (session.auditResponse.raw_response_json?.cumulativeAuditData) {
          const dataAnalysis = this.analyzeDataStructure(
            session.auditResponse.raw_response_json.cumulativeAuditData
          );
          
          sessionVerification.dataAnalysis = dataAnalysis;
          
          if (!dataAnalysis.structureValidation.isWellFormed) {
            sessionVerification.issues.push(`Data structure issues: ${dataAnalysis.structureValidation.errors.join(', ')}`);
            sessionVerification.isValid = false;
          }
          
          if (dataAnalysis.totalChecks === 0) {
            sessionVerification.issues.push('No audit data found');
            verification.summary.missingData++;
          }
        } else if (session.auditResponse.status === 'DRAFT') {
          sessionVerification.issues.push('Draft session missing cumulative data');
          verification.summary.missingData++;
        }
        
        // Verify server existence for active sessions
        if (session.auditResponse.status === 'DRAFT') {
          try {
            verification.serverVerification.attempted++;
            const serverResult = await this.auditService.getAuditResponse(
              session.auditResponse.audit_form_response_id
            );
            
            if (serverResult.success) {
              verification.serverVerification.successful++;
              sessionVerification.serverStatus = 'verified';
              
              // Check for data mismatches
              if (serverResult.auditResponse.updated_at !== session.auditResponse.updated_at) {
                sessionVerification.issues.push('Server data is newer than local data');
                verification.summary.serverMismatches++;
              }
            } else {
              verification.serverVerification.failed++;
              sessionVerification.issues.push('Session not found on server');
              sessionVerification.serverStatus = 'not_found';
              sessionVerification.isValid = false;
            }
          } catch (error) {
            verification.serverVerification.failed++;
            sessionVerification.issues.push(`Server verification failed: ${error.message}`);
            sessionVerification.serverStatus = 'error';
          }
        }
        
      } catch (error) {
        sessionVerification.issues.push(`Verification error: ${error.message}`);
        sessionVerification.isValid = false;
      }
      
      if (sessionVerification.isValid) {
        verification.summary.validSessions++;
      } else {
        verification.summary.corruptedSessions++;
        verification.issues.push(...sessionVerification.issues.map(issue => 
          `Session ${session.sessionId}: ${issue}`
        ));
      }
      
      verification.sessions.push(sessionVerification);
    }
    
    // Calculate integrity score
    verification.summary.integrityScore = verification.summary.totalSessions > 0 ? 
      Math.round((verification.summary.validSessions / verification.summary.totalSessions) * 100) : 100;
    
    // Generate recommendations
    verification.recommendations = this.generateIntegrityRecommendations(verification);
    
    console.log(`✅ Data integrity check completed: ${verification.summary.validSessions}/${verification.summary.totalSessions} sessions valid (${verification.summary.integrityScore}%)`);
    
    // Log verification
    this.debugLog.push({
      timestamp: new Date().toISOString(),
      type: 'integrity_verification',
      verification: {
        verificationId: verification.verificationId,
        summary: verification.summary,
        integrityScore: verification.summary.integrityScore
      }
    });
    
    return verification;
  }

  // Generate integrity-specific recommendations
  generateIntegrityRecommendations(verification) {
    const recommendations = [];
    
    if (verification.summary.corruptedSessions > 0) {
      recommendations.push({
        priority: 'high',
        action: 'Clean up corrupted sessions',
        description: `Remove ${verification.summary.corruptedSessions} corrupted session(s) that cannot be recovered`,
        code: 'cleanup_corrupted'
      });
    }
    
    if (verification.summary.serverMismatches > 0) {
      recommendations.push({
        priority: 'medium',
        action: 'Sync with server',
        description: `${verification.summary.serverMismatches} session(s) have newer data on server. Consider refreshing local data`,
        code: 'sync_server_data'
      });
    }
    
    if (verification.summary.missingData > 0) {
      recommendations.push({
        priority: 'medium',
        action: 'Review empty sessions',
        description: `${verification.summary.missingData} session(s) have no audit data. Verify if these are expected`,
        code: 'review_empty_sessions'
      });
    }
    
    if (verification.serverVerification.failed > verification.serverVerification.successful) {
      recommendations.push({
        priority: 'high',
        action: 'Check connectivity',
        description: 'More server verifications failed than succeeded. Check network connectivity',
        code: 'check_connectivity'
      });
    }
    
    if (verification.summary.integrityScore < 80) {
      recommendations.push({
        priority: 'critical',
        action: 'Emergency recovery',
        description: 'Data integrity is below acceptable threshold. Consider running emergency recovery',
        code: 'emergency_recovery'
      });
    }
    
    return recommendations;
  }

  // Advanced error tracking
  recordError(error, context = {}) {
    const errorRecord = {
      timestamp: new Date().toISOString(),
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      message: error.message || error.toString(),
      stack: error.stack,
      type: error.constructor.name,
      context: {
        currentSession: this.auditService.sessionManager.getCurrentSessionId(),
        currentAuditId: this.auditService.currentAuditResponse?.audit_form_response_id,
        isDraftMode: this.auditService.isDraftMode,
        queueSize: this.auditService.saveQueue.size,
        ...context
      }
    };
    
    this.errorHistory.push(errorRecord);
    
    // Keep only last 50 errors
    if (this.errorHistory.length > 50) {
      this.errorHistory = this.errorHistory.slice(-50);
    }
    
    console.error(`🐛 Error recorded [${errorRecord.errorId}]:`, errorRecord);
    
    return errorRecord.errorId;
  }

  // Diagnostic tests
  async runDiagnostics() {
    console.log('🔬 Running comprehensive diagnostics...');
    
    const diagnostics = {
      timestamp: new Date().toISOString(),
      diagnosticId: `diag_${Date.now()}`,
      tests: [],
      summary: {
        totalTests: 0,
        passedTests: 0,
        failedTests: 0,
        warningTests: 0
      }
    };
    
    // Test 1: Service initialization
    const serviceTest = this.testServiceInitialization();
    diagnostics.tests.push(serviceTest);
    
    // Test 2: Storage functionality
    const storageTest = this.testStorageFunctionality();
    diagnostics.tests.push(storageTest);
    
    // Test 3: Session management
    const sessionTest = this.testSessionManagement();
    diagnostics.tests.push(sessionTest);
    
    // Test 4: Data transformation
    const transformTest = this.testDataTransformation();
    diagnostics.tests.push(transformTest);
    
    // Test 5: API connectivity (if possible)
    const apiTest = await this.testApiConnectivity();
    diagnostics.tests.push(apiTest);
    
    // Calculate summary
    diagnostics.tests.forEach(test => {
      diagnostics.summary.totalTests++;
      if (test.result === 'pass') {
        diagnostics.summary.passedTests++;
      } else if (test.result === 'fail') {
        diagnostics.summary.failedTests++;
      } else if (test.result === 'warning') {
        diagnostics.summary.warningTests++;
      }
    });
    
    diagnostics.overallResult = diagnostics.summary.failedTests === 0 ? 
      (diagnostics.summary.warningTests === 0 ? 'healthy' : 'warnings') : 'issues';
    
    console.log(`🔬 Diagnostics completed: ${diagnostics.summary.passedTests}/${diagnostics.summary.totalTests} tests passed`);
    
    return diagnostics;
  }

  // Individual diagnostic tests
  testServiceInitialization() {
    const test = {
      name: 'Service Initialization',
      result: 'pass',
      issues: [],
      details: {}
    };
    
    try {
      // Check if all sub-modules are properly initialized
      if (!this.auditService.transformer) {
        test.issues.push('Transformer not initialized');
        test.result = 'fail';
      }
      
      if (!this.auditService.sessionManager) {
        test.issues.push('Session manager not initialized');
        test.result = 'fail';
      }
      
      if (!this.auditService.performanceMonitor) {
        test.issues.push('Performance monitor not initialized');
        test.result = 'fail';
      }
      
      // Check service properties
      test.details.independentMode = this.auditService.independentMode;
      test.details.isDraftMode = this.auditService.isDraftMode;
      test.details.hasCurrentAudit = !!this.auditService.currentAuditResponse;
      
    } catch (error) {
      test.result = 'fail';
      test.issues.push(`Initialization test failed: ${error.message}`);
    }
    
    return test;
  }

  testStorageFunctionality() {
    const test = {
      name: 'Storage Functionality',
      result: 'pass',
      issues: [],
      details: {}
    };
    
    try {
      // Test localStorage availability
      if (typeof Storage === 'undefined') {
        test.result = 'fail';
        test.issues.push('localStorage not available');
        return test;
      }
      
      // Test read/write operations
      const testKey = 'audit_debug_test';
      const testData = { test: true, timestamp: Date.now() };
      
      localStorage.setItem(testKey, JSON.stringify(testData));
      const retrieved = JSON.parse(localStorage.getItem(testKey));
      localStorage.removeItem(testKey);
      
      if (!retrieved || retrieved.test !== true) {
        test.result = 'fail';
        test.issues.push('Storage read/write test failed');
      }
      
      // Check storage usage
      const storageInfo = this.calculateLocalStorageSize();
      test.details.storageUsage = storageInfo;
      
      if (storageInfo.mb > 5) {
        test.result = 'warning';
        test.issues.push(`High storage usage: ${storageInfo.mb}MB`);
      }
      
    } catch (error) {
      test.result = 'fail';
      test.issues.push(`Storage test failed: ${error.message}`);
    }
    
    return test;
  }

  testSessionManagement() {
    const test = {
      name: 'Session Management',
      result: 'pass',
      issues: [],
      details: {}
    };
    
    try {
      const sessions = this.auditService.sessionManager.getAllStoredSessions();
      test.details.totalSessions = sessions.length;
      test.details.currentSessionId = this.auditService.sessionManager.getCurrentSessionId();
      
      // Test session data integrity
      let validSessions = 0;
      sessions.forEach(session => {
        try {
          const summary = this.auditService.sessionManager.getSessionSummary(session.sessionId);
          if (summary) {
            validSessions++;
          }
        } catch (error) {
          test.issues.push(`Session ${session.sessionId} validation failed`);
        }
      });
      
      test.details.validSessions = validSessions;
      
      if (validSessions < sessions.length) {
        test.result = 'warning';
        test.issues.push(`${sessions.length - validSessions} invalid sessions found`);
      }
      
      if (sessions.length > 10) {
        test.result = 'warning';
        test.issues.push(`High number of stored sessions: ${sessions.length}`);
      }
      
    } catch (error) {
      test.result = 'fail';
      test.issues.push(`Session management test failed: ${error.message}`);
    }
    
    return test;
  }

  testDataTransformation() {
    const test = {
      name: 'Data Transformation',
      result: 'pass',
      issues: [],
      details: {}
    };
    
    try {
      // Test cumulative data transformation
      const sampleData = {
        '1': {
          categoryName: 'Test Category',
          controlItems: {
            '1': {
              controlItemName: 'Test Control Item',
              checks: {
                '1': { isChecked: true, comment: 'Test failure', originalCheck: { deduction_points: 5 } },
                '2': { isChecked: false, comment: 'Test pass' }
              }
            }
          }
        }
      };
      
      const sampleFormData = {
        hierarchy: {
          checks: [{ check_id: 1, check_code: 'Test Check', deduction_points: 5 }],
          categories: [{ category_id: 1, category_name: 'Test Category' }],
          control_items: [{ control_item_id: 1, control_item_code: 'Test Control Item' }]
        }
      };
      
      const transformResult = this.auditService.transformer.transformCumulativeAuditDataToFieldResponses(
        sampleData, 
        sampleFormData
      );
      
      test.details.transformedCount = transformResult.length;
      test.details.expectedCount = 1; // Should transform 1 failed check
      
      if (!Array.isArray(transformResult)) {
        test.result = 'fail';
        test.issues.push('Transformation did not return array');
      } else if (transformResult.length !== 1) {
        test.result = 'warning';
        test.issues.push(`Expected 1 field response, got ${transformResult.length}`);
      }
      
      // Test format detection
      const isDetectedCorrectly = this.auditService.transformer.isCumulativeDataFormat(sampleData);
      test.details.formatDetection = isDetectedCorrectly;
      
      if (!isDetectedCorrectly) {
        test.result = 'fail';
        test.issues.push('Format detection failed');
      }
      
    } catch (error) {
      test.result = 'fail';
      test.issues.push(`Data transformation test failed: ${error.message}`);
    }
    
    return test;
  }

  async testApiConnectivity() {
    const test = {
      name: 'API Connectivity',
      result: 'pass',
      issues: [],
      details: {}
    };
    
    try {
      if (!this.auditService.currentAuditResponse) {
        test.result = 'skip';
        test.issues.push('No current audit response to test with');
        test.details.reason = 'No active audit session';
        return test;
      }
      
      const startTime = Date.now();
      const result = await this.auditService.getAuditResponse(
        this.auditService.currentAuditResponse.audit_form_response_id,
        { useCache: false }
      );
      const responseTime = Date.now() - startTime;
      
      test.details.responseTime = responseTime;
      test.details.success = result.success;
      
      if (!result.success) {
        test.result = 'fail';
        test.issues.push(`API call failed: ${result.error}`);
      } else if (responseTime > 5000) {
        test.result = 'warning';
        test.issues.push(`Slow API response: ${responseTime}ms`);
      }
      
    } catch (error) {
      test.result = 'fail';
      test.issues.push(`API connectivity test failed: ${error.message}`);
    }
    
    return test;
  }

  // Export debug data
  exportDebugData() {
    return {
      exportedAt: new Date().toISOString(),
      debugLog: this.debugLog,
      errorHistory: this.errorHistory,
      currentState: this.generateDebugReport(false),
      browserInfo: this.getBrowserDebugInfo()
    };
  }

  // Clear debug history
  clearDebugHistory() {
    this.debugLog = [];
    this.errorHistory = [];
    console.log('🧹 Debug history cleared');
  }

  // Reset debugger
  reset() {
    this.clearDebugHistory();
    console.log('🔄 Debugger reset complete');
  }
}